import { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight } from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { AnswerValue, PhaseInfo, WorkflowStage } from '../../types/workflow'

interface EmailCaptureTemplateProps {
  stage: WorkflowStage
  onSelect: (value: AnswerValue) => void
  onSkip?: () => void
  currentPhase?: PhaseInfo | null
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export default function EmailCaptureTemplate({
  onSelect,
  onSkip,
}: EmailCaptureTemplateProps) {
  const [email, setEmail] = useState('')
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const isValid = isValidEmail(email)

  const handleSave = () => {
    if (!isValid) return
    onSelect({ recovery_email: email, marketing_opt_in: marketingOptIn, email_captured: true })
  }

  const handleSkip = () => {
    if (onSkip) {
      onSkip()
    } else {
      onSelect({ recovery_email: null, marketing_opt_in: false, email_captured: false })
    }
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mb-4">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="min-h-[52px] w-full rounded-xl border border-[var(--ww-glass-border)] bg-[var(--ww-surface)] px-4 text-lg text-[var(--ww-text)] placeholder-[var(--ww-text-3)] focus:ring-2 focus:ring-[var(--ww-accent)] focus:border-[var(--ww-accent)]"
        />
        <p className="mt-2 text-center text-xs text-[var(--ww-text-3)]">Optional</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <button
          onClick={() => setMarketingOptIn(!marketingOptIn)}
          className="flex w-full items-start gap-3 rounded-xl p-3 text-left transition-colors hover:bg-[var(--ww-surface)]"
        >
          <div
            className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded transition-colors ${
              marketingOptIn ? 'bg-[var(--ww-accent)]' : 'border border-[var(--ww-glass-border)]'
            }`}
          >
            {marketingOptIn && (
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M2 6L5 9L10 3" stroke="#0a0a0a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
          <span className="text-sm text-[var(--ww-text-2)]">Send me helpful tips about GLP-1 therapy</span>
        </button>
      </motion.div>

      <div className="mt-auto pt-6 pb-2 -ml-[42px] w-[calc(100%+42px)] space-y-3">
        <button
          onClick={handleSave}
          disabled={!isValid}
          className="flex w-full py-4 px-6 items-center justify-center gap-2 text-[15px] font-semibold text-[var(--ww-btn-text)] bg-[var(--ww-btn-bg)] border-none rounded-full cursor-pointer transition-all duration-300 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:not-disabled:bg-[var(--ww-btn-hover)] hover:not-disabled:-translate-y-0.5 disabled:opacity-25 disabled:cursor-default"
        >
          Save & Continue
          <ArrowRight className="h-5 w-5" />
        </button>
        <button onClick={handleSkip} className="w-full py-3 text-sm font-medium text-[var(--ww-text-3)] transition-colors hover:text-[var(--ww-text)]">
          Skip for now
        </button>
      </div>
    </div>
  )
}
